import type {
	LessonSortType,
	SortOrder,
} from "@memorang/ui/components/LessonsExploreScreen/types";
import type { Lesson } from "@memorang/ui/components/LessonsHomeScreen/types";
import { RECENT_WINDOW_DAYS, exploreLessonDetails } from "./constants";

export type FilterCriteria = {
	searchQuery: string;
	selectedTopics: string[];
	isRecentSelected: boolean;
	isCompletedSelected: boolean;
	isPremiumSelected: boolean;
};

export const isLessonSortType = (value: string): value is LessonSortType =>
	value === "popular" ||
	value === "speciality" ||
	value === "difficulty" ||
	value === "recent" ||
	value === "completed";

const matchesSearch = (lesson: Lesson, query: string) => {
	const needle = query.trim().toLowerCase();
	if (!needle) {
		return true;
	}
	return (
		lesson.title.toLowerCase().includes(needle) ||
		lesson.description.toLowerCase().includes(needle)
	);
};

const sortValue = (lesson: Lesson, sortType: LessonSortType) => {
	const details = exploreLessonDetails[lesson.id];
	if (!details) {
		return 0;
	}
	switch (sortType) {
		case "popular":
			return details.popularityScore;
		case "difficulty":
			return details.difficultyLevel;
		case "recent":
			return -details.updatedDaysAgo;
		case "completed":
			return details.isCompleted ? 1 : 0;
		default:
			return 0;
	}
};

export const lessonMatchesFilters = (
	lesson: Lesson,
	criteria: FilterCriteria,
) => {
	if (!matchesSearch(lesson, criteria.searchQuery)) {
		return false;
	}
	if (
		criteria.selectedTopics.length > 0 &&
		!criteria.selectedTopics.includes(lesson.meta.specialty)
	) {
		return false;
	}
	const details = exploreLessonDetails[lesson.id];
	if (!details) {
		return !(
			criteria.isRecentSelected ||
			criteria.isCompletedSelected ||
			criteria.isPremiumSelected
		);
	}
	if (
		criteria.isRecentSelected &&
		details.updatedDaysAgo > RECENT_WINDOW_DAYS
	) {
		return false;
	}
	if (criteria.isCompletedSelected && !details.isCompleted) {
		return false;
	}
	if (criteria.isPremiumSelected && !details.isPremium) {
		return false;
	}
	return true;
};

export const compareLessons = (
	a: Lesson,
	b: Lesson,
	sortType: LessonSortType,
	sortOrder: SortOrder,
) => {
	const comparison =
		sortType === "speciality"
			? a.meta.specialty.localeCompare(b.meta.specialty)
			: sortValue(a, sortType) - sortValue(b, sortType);
	return sortOrder === "asc" ? comparison : -comparison;
};
